import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import useCartStorage from '@/hooks/useCartStorage';
import { cartLabels } from '@/data/content';
import { trackEvent } from '@/Services/analytics';

const HIGHLIGHT_DURATION = 2000;

export interface CartItem {
    id: number;
    name: string;
    price: number;
    quantity: number;
    options?: Record<string, unknown>;
    date?: string;
    period?: string;
    fulfilment?: string;
    weight_percentage?: number;
    menu_id?: number;
    menu_name?: string;
    menu_instance_id?: string;
    menu_group_name?: string;
    portion_quantity?: number;
    food_unit?: string;
    portion_label?: string;
    [key: string]: unknown;
}

export type CartProductInput = {
    id: number;
    price: number;
    name?: string;
    date?: string;
    period?: string;
    fulfilment?: string;
    weight_percentage?: number;
    menu_id?: number;
    menu_name?: string;
    menu_instance_id?: string;
    menu_group_name?: string;
    portion_quantity?: number;
    food_unit?: string;
    portion_label?: string;
    [key: string]: unknown;
};

interface CartContextValue {
    cartItems: CartItem[];
    addToCart: (product: CartProductInput, quantity?: number, options?: Record<string, unknown>) => void;
    removeFromCart: (index: number) => void;
    updateQuantity: (index: number, newQuantity: number) => void;
    clearCart: () => void;
    isCartOpen: boolean;
    setIsCartOpen: React.Dispatch<React.SetStateAction<boolean>>;
    toggleCart: () => void;
    getCartTotal: () => number;
    getCartSummary: () => { cartCount: number; cartTotal: number };
    highlightedItemId: number | null;
    invalidItemIds: Set<number>;
    setInvalidItemIds: (ids: Set<number>) => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

export const useCart = (): CartContextValue => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within CartProvider');
    }
    return context;
};

export const isValidCartItem = (item: unknown): item is CartItem =>
    item !== null &&
    typeof item === 'object' &&
    typeof (item as CartItem).id === 'number' &&
    typeof (item as CartItem).name === 'string' &&
    typeof (item as CartItem).price === 'number' &&
    (item as CartItem).price > 0 &&
    typeof (item as CartItem).quantity === 'number' &&
    (item as CartItem).quantity > 0 &&
    Number.isInteger((item as CartItem).quantity);

export interface CartOrder {
    date: string;
    period: string;
    fulfilment: string;
    foodItems: { id: number; name: string; price: number; quantity: number; weight_percentage: number }[];
}

export const buildCartOrders = (cartItems: CartItem[]): CartOrder[] => {
    const groups = new Map<string, CartOrder>();
    for (const item of cartItems) {
        const key = `${item.date ?? ''}__${item.period ?? ''}__${item.fulfilment ?? ''}`;
        if (!groups.has(key)) {
            groups.set(key, {
                date: item.date ?? '',
                period: item.period ?? '',
                fulfilment: item.fulfilment ?? '',
                foodItems: [],
            });
        }
        groups.get(key)!.foodItems.push({
            id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            weight_percentage: Number(item.weight_percentage ?? 0),
        });
    }
    return [...groups.values()];
};

export interface CartGroup {
    key: string;
    date: string | undefined;
    items: { item: CartItem; index: number }[];
}

export const groupCartItems = (cartItems: CartItem[], fallbackDate?: string): CartGroup[] => {
    const groups: CartGroup[] = [];
    cartItems.forEach((item, index) => {
        const date = item.date || fallbackDate || undefined;
        const key = date ?? 'no-date';
        const existing = groups.find((g) => g.key === key);
        if (existing) {
            existing.items.push({ item, index });
        } else {
            groups.push({ key, date, items: [{ item, index }] });
        }
    });
    return groups;
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { cartItems, addItem, updateItem, removeItem, clearItems } = useCartStorage();
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [highlightedItemId, setHighlightedItemId] = useState<number | null>(null);
    const [invalidItemIds, setInvalidItemIds] = useState<Set<number>>(new Set());
    const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const addToCart: CartContextValue['addToCart'] = useCallback((product, quantity = 1, options = {}) => {
        addItem(product, quantity, options);
        setHighlightedItemId(typeof product.id === 'number' ? product.id : null);
        trackEvent('add_to_cart', {
            item_id: product?.id,
            item_name: product?.name || cartLabels.product_fallback_label,
            quantity: Number(quantity || 1),
            value: Number(product?.price || 0) * Number(quantity || 1),
        });

        if (highlightTimerRef.current !== null) {
            clearTimeout(highlightTimerRef.current);
        }
        highlightTimerRef.current = setTimeout(() => {
            highlightTimerRef.current = null;
            setHighlightedItemId((current) => (current === product.id ? null : current));
        }, HIGHLIGHT_DURATION);
    }, [addItem]);

    const removeFromCart = useCallback((index: number) => {
        const item = cartItems[index];
        removeItem(index);
        if (item) {
            trackEvent('cart_item_removed', {
                item_id: item.id,
                item_name: item.name,
            });
        }
    }, [cartItems, removeItem]);

    const updateQuantity = useCallback((index: number, newQuantity: number) => {
        const item = cartItems[index];
        updateItem(index, newQuantity);
        if (item) {
            trackEvent('cart_quantity_updated', {
                item_id: item.id,
                item_name: item.name,
                new_quantity: newQuantity,
            });
        }
    }, [cartItems, updateItem]);

    const clearCart = useCallback(() => {
        trackEvent('cart_cleared', {
            item_count: cartItems.length,
            cart_total: cartItems.reduce((t, i) => t + i.price * i.quantity, 0),
        });
        clearItems();
    }, [cartItems, clearItems]);

    const toggleCart = useCallback(() => setIsCartOpen((prev) => !prev), []);

    const getCartTotal = useCallback(() => cartItems.reduce((total, item) => total + item.price * item.quantity, 0), [cartItems]);

    const cartSummary = useMemo(
        () => cartItems.reduce((acc, item) => ({ cartCount: acc.cartCount + item.quantity, cartTotal: acc.cartTotal + item.price * item.quantity }), { cartCount: 0, cartTotal: 0 }),
        [cartItems]
    );

    const getCartSummary = useCallback(() => cartSummary, [cartSummary]);

    return (
        <CartContext.Provider
            value={{
                cartItems,
                addToCart,
                removeFromCart,
                updateQuantity,
                clearCart,
                isCartOpen,
                setIsCartOpen,
                toggleCart,
                getCartTotal,
                getCartSummary,
                highlightedItemId,
                invalidItemIds,
                setInvalidItemIds,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};
